import React, {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import { Box } from '@atlaskit/primitives/compiled';

interface WidthObserverContextProps {
	registerElement: (id: string, element: HTMLElement) => void;
	unregisterElement: (id: string) => void;
	subscribe: (id: string, callback: (width: number) => void) => () => void;
}

const WidthObserverContext = createContext<WidthObserverContextProps | undefined>(undefined);

export const WidthObserverProvider = ({
	children,
}: PropsWithChildren<unknown>): React.JSX.Element => {
	const [elements, setElements] = useState<Record<string, HTMLElement>>({});
	const [callbacks, setCallbacks] = useState<Record<string, ((width: number) => void)[]>>({});

	useEffect(() => {
		const handleResize = (entries: ResizeObserverEntry[]) => {
			entries.forEach((entry) => {
				const id = entry.target.getAttribute('data-width-observer-id');
				if (id && callbacks[id]) {
					const width = entry.contentRect.width;
					callbacks[id].forEach((callback) => callback(width));
				}
			});
		};

		const observer = new ResizeObserver(handleResize);

		Object.values(elements).forEach((element) => observer.observe(element));

		return () => {
			observer.disconnect();
		};
	}, [elements, callbacks]);

	const registerElement = useCallback(
		(id: string, element: HTMLElement) => {
			setElements((prev) => ({ ...prev, [id]: element }));
		},
		[setElements],
	);

	const unregisterElement = useCallback(
		(id: string) => {
			setElements((prev) => {
				const newElements = { ...prev };
				delete newElements[id];
				return newElements;
			});
		},
		[setElements],
	);

	const subscribe = useCallback(
		(id: string, callback: (width: number) => void) => {
			setCallbacks((prev) => ({
				...prev,
				[id]: [...(prev[id] || []), callback],
			}));
			return () => {
				setCallbacks((prev) => ({
					...prev,
					[id]: (prev[id] || []).filter((cb) => cb !== callback),
				}));
			};
		},

		[setCallbacks],
	);

	return (
		<WidthObserverContext.Provider value={{ registerElement, unregisterElement, subscribe }}>
			{children}
		</WidthObserverContext.Provider>
	);
};

export const useWidthObserver = () => {
	const context = useContext(WidthObserverContext);
	if (!context) {
		throw new Error('useWidthObserver must be used within a WidthObserverProvider');
	}
	return context;
};

export const useWidthObserverRegistration = <T extends HTMLElement | null>(
	id: string,
	ref: React.RefObject<T>,
): void => {
	const { registerElement, unregisterElement } = useWidthObserver();

	useEffect(() => {
		const element = ref.current;

		if (element) {
			element.setAttribute('data-width-observer-id', id);
			registerElement(id, element);
		}
		return () => {
			unregisterElement(id);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		id,
		ref,
		ref.current, // This is required as the useEffect is not triggered when ref.current becomes defined
		registerElement,
		unregisterElement,
	]);
};

export const WidthObserverElement = ({
	id,
	children,
}: {
	id: string;
	children: React.ReactElement;
}): React.JSX.Element => {
	const elementRef = useRef<HTMLElement>(null);
	useWidthObserverRegistration(id, elementRef);

	return <Box ref={elementRef}>{children}</Box>;
};

export const useElementWidth = (id: string) => {
	const { subscribe } = useWidthObserver();
	const [width, setWidth] = useState(0);

	useEffect(() => {
		const unsubscribe = subscribe(id, setWidth);
		return () => {
			unsubscribe();
		};
	}, [id, subscribe]);

	return width;
};

export const WidthObserverSubscriber = ({
	target: id,
	children,
}: {
	target: string;
	children: (width: number) => JSX.Element;
}) => {
	const width = useElementWidth(id);

	if (width === 0) {
		return null;
	}

	return children(width);
};

const breakpoints = {
	xxs: 0,
	xs: 480,
	sm: 768,
	md: 1024,
	lg: 1440,
	xl: 1768,
} as const;

export const isBreakpointActive = ({
	width,
	above,
	below,
}: {
	width: number;
	above?: keyof typeof breakpoints;
	below?: keyof typeof breakpoints;
}): boolean => {
	return (above && width > breakpoints[above]) || (below && width < breakpoints[below]) || false;
};

interface ShowHideProps {
	target: string;
	above?: keyof typeof breakpoints;
	below?: keyof typeof breakpoints;
	children: React.ReactNode;
}

export const Show = ({ target, above, below, children }: ShowHideProps): React.JSX.Element => {
	return (
		<WidthObserverSubscriber target={target}>
			{(width) => {
				if (isBreakpointActive({ width, above, below })) {
					return <>{children}</>;
				}
				return <></>;
			}}
		</WidthObserverSubscriber>
	);
};

export const Hide = ({ target, above, below, children }: ShowHideProps): React.JSX.Element => {
	return (
		<WidthObserverSubscriber target={target}>
			{(width) => {
				if (isBreakpointActive({ width, above, below })) {
					return <></>;
				}
				return <>{children}</>;
			}}
		</WidthObserverSubscriber>
	);
};
